import clsx from 'clsx'
import React, { useRef } from 'react'
import { useEffect } from 'react'
import { Control } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'

import s from './styles.module.scss'

import { LocationPickerMobileFooter } from './components/LocationPickerMobileFooter'
import { LocationPickerModeEnum } from './location-picker.enum'
import { MostBookedRegionType } from './location-picker.type'
import { getLocationPickerClassNameByEnum } from './location-picker.utils'
import { LocationPickerContentModal } from './LocationPickerContentModal'
import { LocationPickerInputField } from './LocationPickerInputField'
import { useLocationPicker } from './useLocationPicker'
import { ModalPicker, ModalPickerRef } from '../../forms/modal-picker'
import { Inputs } from '../../molecules/search-city-dates-adults-v2/useFieldNavigator'

export interface ControlledLocationPickerProps {
  control: Control<any, any>
  rules: any
  name: any
  message?: string
  className?: string
  containerClassName?: string
  modalClassName?: string
  disabled?: boolean
  maxLocation?: number
  isCentered?: boolean
  renderInputField?: ({
    inputRef,
    onInputChange,
    onReset,
  }: {
    inputRef: React.LegacyRef<HTMLInputElement> | undefined
    onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    onReset?: () => void
  }) => JSX.Element
  renderSearchBarPicker?: ({ value }: { value: string }) => JSX.Element
  onChangeEditMode?: (editing: boolean) => void
  locationTypes: string[]
  shortLabel?: boolean
  isMobile: boolean
  alternativeClassName?: string
  mode?: LocationPickerModeEnum
  mostBookedRegions?: MostBookedRegionType[]
  revampPicker?: boolean
  isFullScreen?: boolean
  onClose?: () => void
  onValid?: (value: keyof Inputs) => void
  isOpen?: boolean
}

export default function LocationPicker({
  control,
  rules,
  name,
  className,
  containerClassName,
  modalClassName,
  renderInputField,
  renderSearchBarPicker,
  disabled = false,
  onChangeEditMode,
  locationTypes,
  shortLabel = false,
  isMobile,
  alternativeClassName = 'default',
  mode = LocationPickerModeEnum.DEFAULT,
  revampPicker = false,
  mostBookedRegions,
  isFullScreen,
  onClose,
  onValid,
  isOpen = false,
}: ControlledLocationPickerProps) {
  const ref = useRef<ModalPickerRef>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const {
    isEditing,
    onEditing,
    searchQuery,
    searchQueryIsEmpty,
    onSearchQuery,
    onInputChange,
    placePredictions,
    isPlacePredictionsLoading,
    isLocationSelected,
    onLocationSelected,
  } = useLocationPicker({ locationTypes })

  useEffect(() => {
    if (onChangeEditMode) {
      onChangeEditMode(isEditing)
    }
  }, [isEditing, onChangeEditMode])

  useEffect(() => {
    if (isOpen) {
      ref.current?.setShowModal(true)
    }
  }, [isOpen])

  const onCloseModal = () => {
    ref.current?.setShowModal(false)
  }

  const onOpenCallback = () => {
    onEditing(true)
    inputRef.current?.focus()
  }
  const onCloseCallback = () => {
    onEditing(false)
    onClose?.()
  }

  return (
    <ModalPicker
      revampPicker={revampPicker}
      isFullScreen={isFullScreen}
      dataGtm={revampPicker ? 'inputLocationNewSearch' : 'inputLocation'}
      dataMixpanel={{
        name: 'inputLocation',
        data: { newSearch: revampPicker },
      }}
      disabled={disabled}
      control={control}
      rules={rules}
      name={name}
      ref={ref}
      className={clsx(className) || s.modalPicker}
      containerClassName={clsx(
        containerClassName,
        s[getLocationPickerClassNameByEnum(mode) + 'Container'],
        s[alternativeClassName],
      )}
      modalClassName={clsx(
        modalClassName
          ? modalClassName
          : s[getLocationPickerClassNameByEnum(mode)],
        {
          [s.hideModal]:
            searchQueryIsEmpty &&
            (!revampPicker || (revampPicker && !mostBookedRegions?.length)),
          [s.revampSearchBar]: revampPicker,
        },
      )}
      innerModalClassName={s.innerModalClassName}
      onOpen={onOpenCallback}
      onClose={onCloseCallback}
      mobileTitle={
        <div>
          {revampPicker ? (
            <FormattedMessage
              id='ui.LocationPicker.revampMobileTitle'
              defaultMessage='Indiquez votre destination'
            />
          ) : (
            <FormattedMessage
              id='ui.LocationPicker.selectOriginPoint'
              defaultMessage="Sélectionnez votre point d'origine"
            />
          )}
        </div>
      }
      mobileFooter={
        revampPicker ? (
          <LocationPickerMobileFooter
            onClick={onCloseModal}
            disabled={!isLocationSelected}
          />
        ) : undefined
      }
      renderContentModal={(field) => (
        <LocationPickerContentModal
          field={field}
          inputRef={inputRef}
          modalRef={ref}
          state={{
            isEditing,
            isMobile: Boolean(isMobile || isFullScreen),
            isPlacePredictionsLoading,
            searchQueryIsEmpty,
            revampPicker,
            shortLabel,
            placePredictions,
            mostBookedRegions,
            searchQuery,
            isLocationSelected,
          }}
          handlers={{
            onEditing,
            onSearchQuery,
            onInputChange,
            onLocationSelected,
            onValid,
          }}
        />
      )}
      renderInputField={(field) => (
        <LocationPickerInputField
          field={field}
          mode={mode}
          alternativeClassName={alternativeClassName}
          inputRef={inputRef}
          handlers={{
            onEditing,
            onSearchQuery,
            onLocationSelected,
            onInputChange,
          }}
          state={{
            isEditing,
            isLocationSelected,
            isMobile: Boolean(isMobile || isFullScreen),
          }}
          renderers={{ renderInputField, renderSearchBarPicker }}
          revampPicker={revampPicker}
        />
      )}
    />
  )
}
